// S1 Sign-in landing. The button is a full-page navigation to the server's Entra sign-in (7.1).
import { ReturnToSchema } from '@feedback/shared/contracts/identity';
import { createFileRoute } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface SignInSearch {
  returnTo?: string;
  error?: string;
}

const ERROR_TEXT: Record<string, string> = {
  sign_in_failed: 'We couldn’t sign you in. Try again. If it keeps happening, ask the Platform admins for help.',
};

export const Route = createFileRoute('/sign-in')({
  // Both keys are always returned (undefined when missing or refused), so a refused raw returnTo can't survive.
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    returnTo:
      typeof search.returnTo === 'string' && ReturnToSchema.safeParse(search.returnTo).success
        ? search.returnTo
        : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: SignIn,
});

function SignIn() {
  const { returnTo, error } = Route.useSearch();
  const href = `/auth/sign-in${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`;
  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="flex flex-col gap-4 p-6">
        <div className="flex items-center gap-2 font-semibold">
          <span className="rounded-sm bg-primary px-1.5 py-0.5 text-xs font-bold text-on-primary">HPG</span>
          Feedback
        </div>
        <h1 className="text-2xl font-semibold">Sign in</h1>
        <p className="text-muted">
          Report a problem or suggest an idea from any HPG app, follow what happens to it, and, if you work on an app,
          review and act on what people send.
        </p>
        {error ? (
          <p role="alert" className="rounded-md border border-danger p-3 text-sm text-danger">
            {ERROR_TEXT[error] ?? ERROR_TEXT.sign_in_failed}
          </p>
        ) : null}
        <Button asChild size="lg">
          <a href={href}>
            <LogIn aria-hidden="true" />
            Sign in with your HPG account
          </a>
        </Button>
        {returnTo ? <p className="text-xs text-muted">You’ll come back to the page you were opening.</p> : null}
      </CardContent>
    </Card>
  );
}
